import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Form,
  TextField,
  Select,
  Button,
  Toast,
  Banner,
  List,
  ChoiceList,
  Checkbox,
  Avatar,
  Spinner
} from '@shopify/polaris';
import { SaveBar, useAppBridge } from '@shopify/app-bridge-react';
import useAxiosClient from '../axiosClient';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../i18n';

export default function EditPolicy() {
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const appBridge = useAppBridge();
  const { id } = useParams(); // Get the policy ID from URL params
  const { t } = useTranslation();

  const [policyInfo, setPolicyInfo] = useState({
    policyName: '',
    policyType: 'Time Off',
    paidStatus: 'Unpaid',
    units: 'Hours',
    balance: 'Unlimited',
    fullDayDuration: '8',
    requiresApproval: true,
    assignedStaff: []
  });

  const [staffMembers, setStaffMembers] = useState([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(true);
  const [isLoadingPolicy, setIsLoadingPolicy] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const [errors, setErrors] = useState({});
  const originalData = useRef(policyInfo);

  // Fetch policy details
  const fetchPolicyDetails = async () => {
    try {
      setIsLoadingPolicy(true);
      const response = await axiosClient.get(`/policies/${id}`);

      if (response.data.success) {
        const policy = response.data.policy[0]; // Assuming the policy is in an array

        // Update state with policy details
        setPolicyInfo({
          policyName: policy.policy_name || '',
          policyType: policy.policy_type || 'Time Off',
          paidStatus: policy.paid_status || 'Unpaid',
          units: policy.units || 'Hours',
          balance: policy.balance || 'Unlimited',
          fullDayDuration: policy.full_day_duration?.toString() || '8',
          requiresApproval: policy.requires_approval !== false, // Default to true if not specified
          assignedStaff: JSON.parse(policy.assigned_staff) || []
        });

        // Store original data for discard functionality
        originalData.current = {
          policyName: policy.policy_name || '',
          policyType: policy.policy_type || 'Time Off',
          paidStatus: policy.paid_status || 'Unpaid',
          units: policy.units || 'Hours',
          balance: policy.balance || 'Unlimited',
          fullDayDuration: policy.full_day_duration?.toString() || '8',
          requiresApproval: policy.requires_approval !== false,
          assignedStaff: JSON.parse(policy.assigned_staff) || []
        };
      } else {
        setToastContent(t('failed_to_load_policy_details'));
        setShowToast(true);
        navigate('/TimeOff/Policy');
      }
    } catch (error) {
      setToastContent(t('error_loading_policy_details'));
      setShowToast(true);
      navigate('/TimeOff/Policy');
    } finally {
      setIsLoadingPolicy(false);
    }
  };

  const handleChange = (field) => (value) => {
    setPolicyInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!policyInfo.policyName.trim()) {
      newErrors.policyName = t('policy_name_is_required');
    }

    if (!policyInfo.policyType) {
      newErrors.policyType = t('policy_type_is_required');
    }

    if (policyInfo.balance === 'Limited' && policyInfo.balanceAmount) {
      if (isNaN(parseFloat(policyInfo.balanceAmount)) || parseFloat(policyInfo.balanceAmount) <= 0) {
        newErrors.balanceAmount = t('please_enter_a_valid_balance_amount');
      }
    }

    if (!policyInfo.fullDayDuration || policyInfo.fullDayDuration === '0') {
      newErrors.fullDayDuration = t('please_enter_a_valid_duration');
    } else if (isNaN(parseFloat(policyInfo.fullDayDuration))) {
      newErrors.fullDayDuration = t('please_enter_a_valid_number');
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setShowToast(true);
      setToastContent(t('please_fill_in_all_required_fields_correctly'));
    }

    return Object.keys(newErrors).length === 0;
  };

  const fetchStaffMembers = async () => {
    try {
      setIsLoadingStaff(true);
      const response = await axiosClient.get('/getStaff');
      if (response.data.success) {
        setStaffMembers(response.data.staff);
      } else {
        setToastContent(t('failed_to_load_staff_members'));
        setShowToast(true);
      }
    } catch (error) {
      setToastContent(t('error_loading_staff_members'));
      setShowToast(true);
    } finally {
      setIsLoadingStaff(false);
    }
  };

  useEffect(() => {
    // First fetch staff members, then fetch policy details
    fetchStaffMembers().then(() => {
      fetchPolicyDetails();
    });
  }, [id]); // Re-fetch when ID changes

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);

      // Create a properly formatted payload that matches the backend requirements
      const payload = {
        policy_name: policyInfo.policyName,
        policy_type: policyInfo.policyType,
        paid_status: policyInfo.paidStatus,
        units: policyInfo.units,
        balance: policyInfo.balance,
        full_day_duration: parseFloat(policyInfo.fullDayDuration),
        requires_approval: policyInfo.requiresApproval,
        assigned_staff: policyInfo.assignedStaff,
      };

      // Add balance_amount if balance is Limited
      if (policyInfo.balance === 'Limited' && policyInfo.balanceAmount) {
        payload.balance_amount = parseFloat(policyInfo.balanceAmount);
      }

      const response = await axiosClient.post(`/update_policies/${id}`, payload);

      if (response.data.success) {
        originalData.current = policyInfo;
        setIsDirty(false);
        setToastContent(t('policy_updated_successfully'));
        setShowToast(true);
        navigate('/TimeOff/Policy');
        appBridge.saveBar.hide('edit-policy-save-bar');
      } else {
        setToastContent(response.data.message || t('failed_to_update_policy'));
        setShowToast(true);
        appBridge.saveBar.hide('edit-policy-save-bar');
      }
    } catch (error) {
      // Handle validation errors from the backend
      if (error.response && error.response.data && error.response.data.errors) {
        setErrors(error.response.data.errors);
        setToastContent(error.response.data.message || t('validation_error'));
      } else {
        setToastContent(t('error_updating_policy'));
      }
      appBridge.saveBar.hide('edit-policy-save-bar');
      setShowToast(true);
    } finally {
      setIsSaving(false);
      appBridge.saveBar.hide('edit-policy-save-bar');
    }
  };

  const handleDiscard = () => {
    setPolicyInfo(originalData.current);
    setIsDirty(false);
    setErrors({});
    appBridge.saveBar.hide('edit-policy-save-bar');
  };


  const handleBackClick = useCallback(() => {
    if (isDirty) {
      appBridge.saveBar.leaveConfirmation()
        .then(() => {
          navigate("/TimeOff/Policy");
        })
        .catch(() => {
          // Navigation cancelled
        });
    } else {
      navigate("/TimeOff/Policy");
    }
  }, [isDirty, appBridge, navigate]);

  if (isLoadingPolicy || isLoadingStaff) {
    return (
      <Page title={t('edit_policy')} fullWidth>
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <p style={{ marginTop: '1rem' }}>{t('loading_policy_details')}</p>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t('edit_policy')}
      titleMetadata={<span style={{ color: '#6d7175', fontSize: '14px', marginLeft: '8px' }}>{t('editing')}</span>}
      fullWidth
      backAction={{
        content: t('policy_management'),
        onAction: handleBackClick 
      }}
    >
      {Object.keys(errors).length > 0 && (
        <Banner
          title={t('please_correct_the_following_errors')}
          tone="critical"
        >
          <List type="bullet">
            {Object.entries(errors).map(([field, messages]) => (
              <List.Item key={field}>
                {`${field.charAt(0).toUpperCase() + field.slice(1)}: ${Array.isArray(messages) ? messages[0] : messages}`}
              </List.Item>
            ))}
          </List>
        </Banner>
      )}

      <Form onSubmit={handleSave}>
        <Layout>
          {/* Policy Information */}
          <Layout.AnnotatedSection
            title={t('policy_information')}
            description={t('enter_the_details_for_the_policy')}
          >
            <Card sectioned>
              <TextField
                label={t('policy_name')}
                value={policyInfo.policyName}
                onChange={handleChange('policyName')}
                placeholder={t('policy_name')}
                error={errors.policyName && Array.isArray(errors.policyName) ? errors.policyName[0] : errors.policyName}
              />
              <div className='mt-2'></div>
              <Select
                label={t('policy_type')}
                options={[
                  { label: t('time_off'), value: 'Time Off' },
                  { label: t('sick_leave'), value: 'Sick Leave' },
                ]}
                value={policyInfo.policyType}
                onChange={handleChange('policyType')}
                error={errors.policyType}
              />
            </Card>
          </Layout.AnnotatedSection>

          {/* Paid/Unpaid Status */}
          <Layout.AnnotatedSection
            title={t('paid_unpaid_status')}
            description={t('select_whether_the_policy_is_paid_or_unpaid')}
          >
            <Card sectioned>
              <ChoiceList
                title={t('paid_status')}
                choices={[
                  { label: t('unpaid'), value: 'Unpaid' },
                  { label: t('paid'), value: 'Paid' },
                ]}
                selected={[policyInfo.paidStatus]}
                onChange={(value) => handleChange('paidStatus')(value[0])}
              />
            </Card>
          </Layout.AnnotatedSection>

          {/* Full Day Duration */}
          <Layout.AnnotatedSection
            title={t('full_day_duration')}
            description={t('enter_the_duration_for_a_full_day')}
          >
            <Card sectioned>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label={t('full_day_duration')}
                  type="number"
                  value={policyInfo.fullDayDuration}
                  onChange={handleChange('fullDayDuration')}
                  error={errors.fullDayDuration && Array.isArray(errors.fullDayDuration) ? errors.fullDayDuration[0] : errors.fullDayDuration}
                />
                <span style={{ marginLeft: '8px', marginTop: '18px', fontSize: '14px', color: '#202223' }}>{t('hours')}</span>
              </div>
            </Card>
          </Layout.AnnotatedSection>

          {/* Assigned Staff Members */}
          <Layout.AnnotatedSection
            title={t('assigned_staff_members')}
            description={t('select_the_staff_members_to_whom_this_policy_applies')}
          >
            <Card sectioned>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px', fontSize: '14px' }}>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: '12px' }}>
                      <Checkbox
                        label=""
                        checked={policyInfo.assignedStaff.length === staffMembers.length}
                        indeterminate={
                          policyInfo.assignedStaff.length > 0 &&
                          policyInfo.assignedStaff.length < staffMembers.length
                        }
                        onChange={(checked) => {
                          const newSelected = checked
                            ? staffMembers.map((member) => member.id)
                            : [];
                          handleChange('assignedStaff')(newSelected);
                        }}
                      />
                    </th>
                    <th style={{ padding: '12px', color: '#6d7175', fontWeight: '600' }}>{t('staff')}</th>
                    <th style={{ padding: '12px', color: '#6d7175', fontWeight: '600' }}>{t('position')}</th>
                  </tr>
                </thead>
                <tbody>
                  {staffMembers.map((member) => {
                    const isChecked = policyInfo.assignedStaff.includes(member.id);
                    return (
                      <tr
                        key={member.id}
                        style={{
                          backgroundColor: '#ffffff',
                          borderRadius: '8px',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                          transition: 'background 0.2s',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f4f6f8')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                      >
                        <td style={{ padding: '16px 12px' }}>
                          <Checkbox
                            checked={isChecked}
                            onChange={() =>
                              handleChange('assignedStaff')(
                                isChecked
                                  ? policyInfo.assignedStaff.filter((id) => id !== member.id)
                                  : [...policyInfo.assignedStaff, member.id]
                              )
                            }
                          />
                        </td>
                        <td style={{ padding: '16px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar source={member.first_name} size="md" />
                            <span style={{ marginLeft: '12px', fontWeight: '500' }}>
                              {member.first_name} {member.last_name}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '16px 12px', color: '#4a4a4a' }}>{member.position}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </Layout.AnnotatedSection>

        </Layout>
      </Form>

      {isDirty && (
        <SaveBar
          id="edit-policy-save-bar"
          open={isDirty}
        >
          <button
            type="button"
            variant="primary"
            onClick={async () => {
              try {
                await handleSave();
              } catch (err) {
                // Error handled silently
              }
            }}
            disabled={isSaving}
          >
            {isSaving ? t('saving') : t('save')}
          </button>
          <button
            type="button"
            onClick={handleDiscard}
            disabled={isSaving}
          >
            {t('discard')}
          </button>
        </SaveBar>
      )}
    </Page>
  );
}
